import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ClipboardList, Plus, Eye, Pencil, Trash2, Lock, Unlock, Calendar, Users, FilterX,
} from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import {
  PageHeader, AppButton, StatusBadge, ConfirmModal, FilterToolbar, AppDataTable,
  ActionMenu, ActionMenuItem, ActionMenuDivider,
  applyColumnFilters, sortRows, isColumnFilterActive,
} from "../../ui";
import CommonToaster from "../../Common/CommonToaster";
import { useBranchKey } from "../../../contexts/BranchContext";
import { assignmentsBase } from "./assignmentRules";

/**
 * GeneralAssignmentList — text/file assignments, one row per assignment.
 *
 * The table is `ui/AppDataTable` with the header controls on, the same shape as
 * `/sa/institutes`: `/general-assignments/list` returns everything the caller's
 * branch scope allows in one response, so filtering, sorting and paging all
 * happen in the browser over the whole list.
 *
 * ⚠️ The search box is gone. It read the title and the batch name, which are now
 * the Title and Batch columns' own filters — and a value settable from two
 * places is a value that will eventually disagree with itself.
 *
 */

const PAGE_SIZE = 25;

const DASH = <span className="tw-text-content-subtle">—</span>;

const STATUS_FILTER_OPTIONS = [
  { value: "published", label: "Open" },
  { value: "closed", label: "Closed" },
];

const getRoutePrefix = () => assignmentsBase(getCurrentUser()?.usertype);

const GeneralAssignmentList = () => {
  const navigate = useNavigate();
  const branchKey = useBranchKey();

  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [deleteModal, setDeleteModal] = useState({ show: false, id: null, title: "" });
  const [deleting, setDeleting] = useState(false);

  const [columnFilters, setColumnFilters] = useState({});
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);

  const showToast = useCallback(
    (message, type = "success") => setToast({ show: true, message, type }),
    [],
  );

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/general-assignments/list");
      setAssignments(data.assignments || []);
    } catch {
      showToast("Failed to load assignments", "error");
    }
    setLoading(false);
  }, [showToast]);

  // branchKey is part of this list's identity — the API returns a different set
  // per branch, so a branch switch has to re-fetch. See BranchContext.
  useEffect(() => { fetchAssignments(); }, [branchKey, fetchAssignments]);

  const toggleStatus = useCallback(async (id, currentStatus) => {
    try {
      const endpoint = currentStatus === "published" ? "close" : "reopen";
      await apiClient.post(`/general-assignments/${id}/${endpoint}`);
      fetchAssignments();
    } catch {
      showToast("Failed to update status", "error");
    }
  }, [fetchAssignments, showToast]);

  /** Batches present in the data, not the whole batch catalogue — a set filter
   *  offering batches with no assignment is a filter you stop opening. */
  const batchOptions = useMemo(() => {
    const seen = new Set(assignments.map((a) => a.batchId?.name).filter(Boolean));
    return [...seen].sort((a, b) => a.localeCompare(b)).map((n) => ({ value: n, label: n }));
  }, [assignments]);

  const columns = useMemo(() => [
    {
      key: "title",
      label: "Title",
      sortable: true,
      filter: "text",
      filterValue: (a) => a.title || "",
      render: (a) => <span className="tw-font-medium tw-text-content">{a.title}</span>,
    },
    {
      key: "batch",
      label: "Batch",
      sortable: true,
      filter: { type: "set", options: batchOptions },
      filterValue: (a) => a.batchId?.name || "",
      render: (a) => a.batchId?.name || DASH,
    },
    {
      key: "dueDate",
      label: "Due Date",
      sortable: true,
      filter: "date",
      filterValue: (a) => a.dueDate || null,
      render: (a) => (a.dueDate ? (
        <span className="tw-inline-flex tw-items-center tw-gap-1">
          <Calendar size={14} />
          {/* ⚠️ IST explicitly. The box is UTC and a bare toLocaleDateString
              names the previous day for anything due after 05:30 IST. */}
          {new Date(a.dueDate).toLocaleDateString("en-IN", {
            day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata",
          })}
        </span>
      ) : DASH),
    },
    {
      key: "maxMarks",
      label: "Max Marks",
      align: "right",
      sortable: true,
      filter: "number",
      // ⚠️ Zero, never undefined — the number filter rejects a non-finite value.
      filterValue: (a) => a.maxMarks ?? 0,
      render: (a) => <span className="tw-tabular-nums">{a.maxMarks}</span>,
    },
    {
      key: "submissionCount",
      label: "Submissions",
      align: "right",
      sortable: true,
      filter: "number",
      filterValue: (a) => a.submissionCount ?? 0,
      render: (a) => (
        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-tabular-nums">
          <Users size={14} /> {a.submissionCount || 0}
          {a.toGradeCount > 0 && (
            <StatusBadge status="warning">{a.toGradeCount} to grade</StatusBadge>
          )}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      filter: { type: "set", options: STATUS_FILTER_OPTIONS },
      filterValue: (a) => a.status || "",
      render: (a) => (
        <StatusBadge status={a.status} domain="assignment" />
      ),
    },
    {
      key: "actions",
      label: "",
      width: 48,
      pinned: "right",
      resizable: false,
      // Four icon buttons became one menu — and the Close/Reopen one had only a
      // `title`, which screen readers are not required to read.
      //
      // ⚠️ The row is clickable, so every control in this cell must stop the
      // click reaching it — otherwise opening the menu also navigates away.
      render: (a) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <ActionMenu label={`Actions for ${a.title}`}>
            <ActionMenuItem onClick={() => navigate(`${getRoutePrefix()}/${a._id}`)}>
              <Eye size={14} /> View
            </ActionMenuItem>
            <ActionMenuItem onClick={() => navigate(`${getRoutePrefix()}/edit/${a._id}`)}>
              <Pencil size={14} /> Edit
            </ActionMenuItem>
            <ActionMenuItem onClick={() => toggleStatus(a._id, a.status)}>
              {a.status === "published"
                ? <><Lock size={14} /> Close</>
                : <><Unlock size={14} /> Reopen</>}
            </ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem
              danger
              onClick={() => setDeleteModal({ show: true, id: a._id, title: a.title })}
            >
              <Trash2 size={14} /> Delete
            </ActionMenuItem>
          </ActionMenu>
        </div>
      ),
    },
  ], [batchOptions, navigate, toggleStatus]);

  const filtersOn = Object.values(columnFilters).some(isColumnFilterActive);

  const visible = useMemo(
    () => sortRows(applyColumnFilters(assignments, columns, columnFilters), columns, sort),
    [assignments, columns, columnFilters, sort],
  );

  // A filter or a sort can leave the current page past the end of the results.
  useEffect(() => { setPage(1); }, [columnFilters, sort]);
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page],
  );

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await apiClient.delete(`/general-assignments/${deleteModal.id}`);
      showToast("Assignment deleted");
      setDeleteModal({ show: false, id: null, title: "" });
      fetchAssignments();
    } catch {
      showToast("Failed to delete", "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <PageHeader
        icon={ClipboardList}
        title="Batch Assignments"
        description="Text and file work for your batches — set it, collect it, grade it"
      />

      <AppDataTable
        fill={false}
        resizable
        storageKey="general-assignments"
        toolbar={
          <FilterToolbar
            flush
            actions={
              <AppButton size="sm" onClick={() => navigate(`${getRoutePrefix()}/create`)}>
                <Plus size={16} /> Create Assignment
              </AppButton>
            }
          >
            <span className="tw-text-[0.8125rem] tw-text-content-subtle">
              {visible.length === assignments.length
                ? `${assignments.length} assignment${assignments.length === 1 ? "" : "s"}`
                : `${visible.length} of ${assignments.length}`}
            </span>
            {filtersOn && (
              <AppButton size="sm" variant="outline" onClick={() => setColumnFilters({})}>
                <FilterX size={14} /> Clear filters
              </AppButton>
            )}
          </FilterToolbar>
        }
        columns={columns}
        rows={pageRows}
        rowKey={(a) => a._id}
        loading={loading}
        sort={sort}
        onSortChange={(key, direction) => setSort({ key, direction })}
        columnFilters={columnFilters}
        onColumnFilterChange={(key, value) =>
          setColumnFilters((prev) => {
            // Cleared filters are REMOVED, not stored as undefined — a key
            // sitting there holding nothing would keep "Clear filters" on
            // screen after the last one was cleared.
            const next = { ...prev };
            if (value === undefined) delete next[key];
            else next[key] = value;
            return next;
          })
        }
        onRowClick={(a) => navigate(`${getRoutePrefix()}/${a._id}`)}
        emptyMessage={filtersOn
          ? "No assignment matches these filters."
          : "No assignments yet. Create your first general assignment."}
        pagination={totalPages > 1 ? {
          page,
          totalPages,
          total: visible.length,
          pageSize: PAGE_SIZE,
          onChange: setPage,
          infoLabel: "assignments",
        } : undefined}
      />

      <ConfirmModal
        show={deleteModal.show}
        title="Delete Assignment"
        message={`Are you sure you want to delete "${deleteModal.title}"? This will also delete all student submissions.`}
        confirmText="Delete"
        /* ⚠️ `danger`, not `destructive`. ConfirmModal styles four variants —
           danger / warning / success / info — and an unknown one produced
           `confirm-dialog__btn--destructive`, a class no stylesheet defines, so
           the confirm button rendered with no fill at all. */
        variant="danger"
        loading={deleting}
        onConfirm={handleDelete}
        onHide={() => setDeleteModal({ show: false, id: null, title: "" })}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default GeneralAssignmentList;
